'use client';

import { useState } from 'react';
import {
  Rocket,
  Zap,
  Star,
  Heart,
  Target,
  Briefcase,
  Code,
  Database,
  Globe,
  Home,
  Layers,
  Layout,
  MessageSquare,
  Monitor,
  Package,
  Palette,
  Settings,
  Shield,
  ShoppingCart,
  Users,
  Folder,
  FileText,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  BarChart,
  PieChart,
  Activity,
  Award,
  Bell,
  Book,
  Bookmark,
  Box,
  Camera,
  Cloud,
  Coffee,
  Compass,
  Cpu,
  CreditCard,
  Diamond,
  Disc,
  Droplet,
  Eye,
  Feather,
  Film,
  Flag,
  Flame,
  Gift,
  Headphones,
  HelpCircle,
  Image,
  Inbox,
  Key,
  Lightbulb,
  Link,
  Lock,
  Mail,
  Map,
  Music,
  Navigation,
  Paperclip,
  Phone,
  Play,
  Plug,
  Radio,
  Send,
  Server,
  Smartphone,
  Speaker,
  Sun,
  Tag,
  Terminal,
  Thermometer,
  Wrench,
  Trash,
  Truck,
  Tv,
  Umbrella,
  Video,
  Wifi,
  Wind,
  type LucideIcon,
} from 'lucide-react';

const ICONS: { name: string; icon: LucideIcon }[] = [
  { name: 'rocket', icon: Rocket },
  { name: 'zap', icon: Zap },
  { name: 'star', icon: Star },
  { name: 'heart', icon: Heart },
  { name: 'target', icon: Target },
  { name: 'briefcase', icon: Briefcase },
  { name: 'code', icon: Code },
  { name: 'database', icon: Database },
  { name: 'globe', icon: Globe },
  { name: 'home', icon: Home },
  { name: 'layers', icon: Layers },
  { name: 'layout', icon: Layout },
  { name: 'message-square', icon: MessageSquare },
  { name: 'monitor', icon: Monitor },
  { name: 'package', icon: Package },
  { name: 'palette', icon: Palette },
  { name: 'settings', icon: Settings },
  { name: 'shield', icon: Shield },
  { name: 'shopping-cart', icon: ShoppingCart },
  { name: 'users', icon: Users },
  { name: 'folder', icon: Folder },
  { name: 'file-text', icon: FileText },
  { name: 'calendar', icon: Calendar },
  { name: 'clock', icon: Clock },
  { name: 'check-circle', icon: CheckCircle },
  { name: 'alert-circle', icon: AlertCircle },
  { name: 'trending-up', icon: TrendingUp },
  { name: 'bar-chart', icon: BarChart },
  { name: 'pie-chart', icon: PieChart },
  { name: 'activity', icon: Activity },
  { name: 'award', icon: Award },
  { name: 'bell', icon: Bell },
  { name: 'book', icon: Book },
  { name: 'bookmark', icon: Bookmark },
  { name: 'box', icon: Box },
  { name: 'camera', icon: Camera },
  { name: 'cloud', icon: Cloud },
  { name: 'coffee', icon: Coffee },
  { name: 'compass', icon: Compass },
  { name: 'cpu', icon: Cpu },
  { name: 'credit-card', icon: CreditCard },
  { name: 'diamond', icon: Diamond },
  { name: 'disc', icon: Disc },
  { name: 'droplet', icon: Droplet },
  { name: 'eye', icon: Eye },
  { name: 'feather', icon: Feather },
  { name: 'film', icon: Film },
  { name: 'flag', icon: Flag },
  { name: 'flame', icon: Flame },
  { name: 'gift', icon: Gift },
  { name: 'headphones', icon: Headphones },
  { name: 'help-circle', icon: HelpCircle },
  { name: 'image', icon: Image },
  { name: 'inbox', icon: Inbox },
  { name: 'key', icon: Key },
  { name: 'lightbulb', icon: Lightbulb },
  { name: 'link', icon: Link },
  { name: 'lock', icon: Lock },
  { name: 'mail', icon: Mail },
  { name: 'map', icon: Map },
  { name: 'music', icon: Music },
  { name: 'navigation', icon: Navigation },
  { name: 'paperclip', icon: Paperclip },
  { name: 'phone', icon: Phone },
  { name: 'play', icon: Play },
  { name: 'plug', icon: Plug },
  { name: 'radio', icon: Radio },
  { name: 'send', icon: Send },
  { name: 'server', icon: Server },
  { name: 'smartphone', icon: Smartphone },
  { name: 'speaker', icon: Speaker },
  { name: 'sun', icon: Sun },
  { name: 'tag', icon: Tag },
  { name: 'terminal', icon: Terminal },
  { name: 'thermometer', icon: Thermometer },
  { name: 'wrench', icon: Wrench },
  { name: 'trash', icon: Trash },
  { name: 'truck', icon: Truck },
  { name: 'tv', icon: Tv },
  { name: 'umbrella', icon: Umbrella },
  { name: 'video', icon: Video },
  { name: 'wifi', icon: Wifi },
  { name: 'wind', icon: Wind },
];

interface IconPickerProps {
  value?: string;
  onChange: (iconName: string) => void;
}

export function IconPicker({ value, onChange }: IconPickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedIcon = ICONS.find((i) => i.name === value);
  const SelectedIconComponent = selectedIcon?.icon || Rocket;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-12 h-12 rounded-lg border-2 border-dashed border-muted-foreground/30 hover:border-primary/50 flex items-center justify-center transition-colors bg-background"
      >
        <SelectedIconComponent className="w-6 h-6 text-primary" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full left-0 mt-2 w-72 max-h-64 overflow-y-auto bg-popover border rounded-lg shadow-lg z-50 p-2">
            <div className="grid grid-cols-8 gap-1">
              {ICONS.map(({ name, icon: Icon }) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => {
                    onChange(name);
                    setIsOpen(false);
                  }}
                  className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${value === name
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                    }`}
                  title={name}
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function getIconComponent(iconName: string): LucideIcon {
  const found = ICONS.find((i) => i.name === iconName);
  return found?.icon || Folder;
}

export { ICONS };
